import { CaseComplaintPanel } from './complaints-panel.js';
import { CaseAssetPanel } from './assets-panel.js';
import { CaseJobsPanel } from './dispatch-panel.js';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import {
  api,
  type ApiError,
  type GetCaseResult,
  type SuggestedArticlesResult,
} from '@whinerp/client';
import { Button, EmptyState, ErrorState, Icon, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatDateTime } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { slaChip } from './sla.js';
import { defineScreen } from '../types.js';

/**
 * SC-29 Case record.
 *
 * **Two labelled buttons, not a toggle beside Send.** This is the one design
 * decision in the screen that matters. The classic expensive mistake in
 * support software is pressing Send with the toggle in the wrong state, and a
 * toggle is exactly the indirection that makes it possible: the control that
 * decides who sees the message is not the control you press. Two buttons - one
 * saying "Reply to the customer", one saying "Add an internal note" - remove
 * the indirection. There is nothing to be in the wrong state.
 *
 * **The SLA is in the header, not a tab.** An agent decides what to do next
 * from it, and a countdown behind a tab is a countdown nobody sees until the
 * ticket has breached.
 *
 * **A failed send keeps the draft.** Always. A paragraph somebody has just
 * written is worth more than a clean text box.
 */

type CaseDetail = GetCaseResult;
type Articles = SuggestedArticlesResult;

function CaseRecordScreen(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const params = useParams();
  const caseId = params['id'] ?? '';

  const [draft, setDraft] = useState('');
  const [sent, setSent] = useState<'public' | 'internal' | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  const detail = useQuery<CaseDetail, ApiError>({
    queryKey: ['case', caseId],
    queryFn: () => api.getCase({ path: { caseId } }),
  });

  // Its own request: the suggestions are a search and the thread is not, and
  // a slow search must not hold up the conversation somebody is reading.
  const articles = useQuery<Articles, ApiError>({
    queryKey: ['case-articles', caseId],
    queryFn: () => api.suggestedArticles({ path: { caseId } }),
  });

  const reply = useMutation({
    mutationFn: (visibility: 'public' | 'internal') =>
      api.replyToCase({ path: { caseId }, body: { visibility, body: draft } }),
    onSuccess: (_result, visibility) => {
      setDraft('');
      setSent(visibility);
      setFailure(null);
      void queryClient.invalidateQueries({ queryKey: ['case', caseId] });
    },
    // The draft is not cleared. A paragraph somebody has just written is worth
    // more than a clean text box.
    onError: (error: unknown) =>
      setFailure(error instanceof Error ? error.message : t('error.unexpected')),
  });

  const resolve = useMutation({
    mutationFn: () => api.setCaseStatus({ path: { caseId }, body: { status: 'resolved' } }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['case', caseId] }),
  });

  if (detail.isPending) return <CaseLoading />;
  if (detail.isError) return <CaseError error={detail.error} retry={() => void detail.refetch()} />;

  const { supportCase, messages } = detail.data;
  const chip = slaChip(supportCase.sla, format, t);

  return (
    <div data-component="CaseRecord" className="flex w-full min-w-0 flex-col gap-3">
      <header className="flex flex-wrap items-center gap-3">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h1 className="truncate text-h1 text-ink">{supportCase.subject}</h1>
          <p className="text-meta text-ink-meta">
            {supportCase.number} · {supportCase.contactName ?? ''} ·{' '}
            {t(`case.status.${supportCase.status}` as 'case.status.open')}
          </p>
        </div>

        {/*
         * The countdown, in the header.
         *
         * An agent decides what to do next from this. Behind a tab it is a
         * countdown nobody sees until the ticket has breached.
         */}
        <span
          data-sla-header
          data-sla-urgency={chip.urgency}
          className={
            chip.tone === 'danger'
              ? 'inline-flex h-chip items-center gap-1 rounded-chip bg-danger-tint px-2 text-dense text-danger-text'
              : chip.tone === 'warning'
                ? 'inline-flex h-chip items-center gap-1 rounded-chip bg-warning-tint px-2 text-dense text-warning-text'
                : chip.tone === 'success'
                  ? 'inline-flex h-chip items-center gap-1 rounded-chip bg-success-tint px-2 text-dense text-success-text'
                  : 'inline-flex h-chip items-center gap-1 rounded-chip bg-sunken px-2 text-dense text-ink-600'
          }
        >
          <Icon name="Clock" size="inline" decorative />
          {chip.label}
        </span>

        <span className="ms-auto flex gap-2">
          <Button
            intent="secondary"
            data-resolve
            busy={resolve.isPending}
            disabled={supportCase.status === 'resolved' || supportCase.status === 'closed'}
            onClick={() => resolve.mutate()}
          >
            {t('case.resolve')}
          </Button>
        </span>
      </header>

      <div className="flex min-w-0 gap-4">
        {/* Left: the conversation and the composer. */}
        <section aria-label={t('case.subject')} data-pane="thread" className="flex min-w-0 flex-1 flex-col gap-3">
          <Panel>
            <div className="flex flex-col gap-2">
              <SectionLabel>{t('case.reply.draft')}</SectionLabel>
              <label className="flex flex-col gap-1">
                <span className="sr-only">{t('case.reply.draft')}</span>
                <textarea
                  data-reply-draft
                  aria-label={t('case.reply.draft')}
                  value={draft}
                  onChange={(event) => {
                    setDraft(event.target.value);
                    setSent(null);
                  }}
                  rows={4}
                  className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                />
              </label>

              {failure ? (
                <p role="alert" data-reply-failed className="rounded-card bg-warning-tint px-3 py-2 text-dense text-warning-text">
                  {t('case.reply.failed', { values: { reason: failure } })} {t('case.reply.draftKept')}
                </p>
              ) : null}
              {sent ? (
                <p role="status" data-reply-sent={sent} className="rounded-card bg-success-tint px-3 py-2 text-dense text-success-text">
                  {sent === 'public' ? t('case.reply.sent') : t('case.reply.noted')}
                </p>
              ) : null}

              {/*
               * Two buttons, each saying who will see it.
               *
               * Not a toggle beside a generic Send. A toggle is the
               * indirection that makes the classic mistake possible: the
               * control deciding who sees the message is not the control you
               * press. Here there is nothing to be in the wrong state.
               */}
              <div className="flex flex-wrap gap-2">
                <span className="flex flex-col gap-0.5">
                  <Button
                    intent="primary"
                    icon="PaperPlaneTilt"
                    data-reply-public
                    busy={reply.isPending}
                    disabled={draft.trim() === ''}
                    onClick={() => reply.mutate('public')}
                  >
                    {t('case.reply.public')}
                  </Button>
                  <span className="text-meta text-ink-meta">{t('case.reply.publicHelp')}</span>
                </span>
                <span className="flex flex-col gap-0.5">
                  <Button
                    intent="secondary"
                    icon="Lock"
                    data-reply-internal
                    busy={reply.isPending}
                    disabled={draft.trim() === ''}
                    onClick={() => reply.mutate('internal')}
                  >
                    {t('case.reply.internal')}
                  </Button>
                  <span className="text-meta text-ink-meta">{t('case.reply.internalHelp')}</span>
                </span>
              </div>
            </div>
          </Panel>

          {/* Newest first, as 04_Screens asks: the last thing said is what matters. */}
          <ol data-thread className="flex flex-col gap-2">
            {messages.map((message) => (
              <li
                key={message.messageId}
                data-message={message.messageId}
                data-visibility={message.visibility}
                className={
                  message.visibility === 'internal'
                    ? 'flex flex-col gap-0.5 rounded-card border border-dashed border-rule-strong bg-sunken p-2'
                    : 'flex flex-col gap-0.5 rounded-card border border-rule bg-surface p-2'
                }
              >
                <span className="flex flex-wrap items-baseline gap-2 text-meta text-ink-meta">
                  <span>
                    {message.authorName ??
                      t(`case.author.${message.author}` as 'case.author.customer')}
                  </span>
                  <Num className="text-meta">{formatDateTime(message.sentAt, format)}</Num>
                  {/* Said in words on every message, not implied by the
                      border. A handover sheet printed in grey has to carry
                      this. */}
                  <span
                    data-visibility-label={message.visibility}
                    className={
                      message.visibility === 'internal'
                        ? 'ms-auto text-meta text-warning-text'
                        : 'ms-auto text-meta text-ink-meta'
                    }
                  >
                    {message.visibility === 'internal'
                      ? t('case.message.internal')
                      : t('case.message.public')}
                  </span>
                </span>
                <span className="text-body text-ink">{message.body}</span>
              </li>
            ))}
          </ol>
        </section>

        {/* Right: the attributes and the suggestions. */}
        <aside aria-label={t('case.details')} data-pane="details" className="flex w-panel-wide shrink-0 flex-col gap-3">
          {/* On-site visits for this case (G-201). */}
          <CaseJobsPanel caseId={caseId} />
          {/* The asset it is about, and whether it is under warranty (G-202). */}
          <CaseAssetPanel caseId={caseId} />
          {/* A formal complaint and its deadlines (G-203). */}
          <CaseComplaintPanel caseId={caseId} />
          <Panel>
            <dl className="flex flex-col gap-2">
              {(
                [
                  ['case.priority', t(`case.priority.${supportCase.priority}` as 'case.priority.normal')],
                  ['case.assignee', supportCase.assignedToName ?? t('case.unassigned')],
                  ['case.contact', supportCase.contactName ?? ''],
                ] as const
              ).map(([key, value]) => (
                <div key={key}>
                  <dt className="text-micro uppercase tracking-label text-ink-label">
                    {t(key as 'case.priority')}
                  </dt>
                  <dd data-detail={key} className="text-body text-ink">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </Panel>

          <Panel>
            <SectionLabel>{t('case.articles')}</SectionLabel>
            {articles.isPending ? (
              <Skeleton className="h-plot w-full" />
            ) : (articles.data?.articles ?? []).length === 0 ? (
              <p className="text-dense text-ink-meta">{t('case.articles.none')}</p>
            ) : (
              <ul data-articles className="flex flex-col gap-1">
                {(articles.data?.articles ?? []).map((article) => (
                  <li
                    key={article.articleId}
                    data-article={article.articleId}
                    data-article-audience={article.audience}
                    className="flex items-baseline justify-between gap-2 text-dense text-ink"
                  >
                    <span className="truncate">{article.title}</span>
                    {/* An internal runbook is the point of the panel for an
                        agent, and the thing that must never be pasted into a
                        public reply. So it says which it is. */}
                    {article.audience === 'internal' ? (
                      <span className="shrink-0 text-meta text-warning-text">
                        {t('case.articles.internal')}
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )}
          </Panel>
        </aside>
      </div>
    </div>
  );
}

function CaseEmpty(): JSX.Element {
  const t = useT();
  return (
    <EmptyState
      reason="no-records"
      illustration="Lifebuoy"
      title={t('case.empty.title')}
      body={t('case.empty.body')}
      primary={{ label: t('empty.action.newCase'), icon: 'Plus', href: '/cases' }}
    />
  );
}

function CaseLoading(): JSX.Element {
  const t = useT();
  return (
    <div className="flex w-full flex-col gap-3" role="status" aria-live="polite" aria-busy>
      <span className="sr-only">{t('state.loading')}</span>
      <Skeleton className="h-line-h1 w-panel-wide" />
      <div className="flex gap-4">
        <Skeleton className="h-plot flex-1" />
        <Skeleton className="h-plot w-panel-wide" />
      </div>
    </div>
  );
}

function CaseRecordError({ error, retry }: { error: unknown; retry: () => void }): JSX.Element {
  return (
    <ErrorState
      title="This case could not be loaded"
      body={error instanceof Error ? error.message : 'The case did not load.'}
      onRetry={retry}
    />
  );
}

const CaseError = CaseRecordError;

export const screen = defineScreen({
  id: 'SC-29',
  route: '/cases/:id',
  surface: 'page',
  title: 'Case',
  features: ['F-085', 'F-088'],
  entitlement: 'F-085',
  reachedBy: [{ kind: 'screen', from: 'SC-28', how: 'a row in the queue' }],
  components: [],
  elements: ['CaseRecord'],
  sampleRecord: { objectKey: 'case', param: 'id' },
  states: { empty: CaseEmpty, loading: CaseLoading, error: CaseRecordError },
  component: CaseRecordScreen,
});

export default screen;
